/**
 * @file DebugInfo.tsx
 * @description Debug information panel for content generation details.
 * Only shown in non-production environments.
 */

import React from "react";
import ReactMarkdown from "react-markdown";
import { ContentDebugInfo } from "../../types";
import { toast } from "react-toastify";

interface DebugInfoProps {
  contentId: string;
  debugInfo: ContentDebugInfo;
  generatedContent: string;
}

const formatCostDescription = (cost: {
  service: string;
  operation: string;
  model?: string;
  calls?: number;
  inputTokens?: number;
  outputTokens?: number;
}) => {
  if (cost.service === "DataForSEO") {
    return `${cost.service} - ${cost.operation} (${cost.calls} calls)`;
  }

  if (cost.service === "OpenAI") {
    return `${cost.service} - ${cost.model} (${cost.inputTokens}/${cost.outputTokens} token)`;
  }

  return `${cost.service} - ${cost.operation}`;
};

const DebugInfo: React.FC<DebugInfoProps> = React.memo(
  ({ contentId, debugInfo, generatedContent }) => {
    if (!debugInfo || !debugInfo.costs || !debugInfo.exampleContents) {
      return null;
    }

    const { exampleContents, costs } = debugInfo;

    const handleCopyId = async () => {
      try {
        await navigator.clipboard.writeText(contentId);
        toast.success("ID copiato negli appunti");
      } catch (err) {
        toast.error("Impossibile copiare l'ID. Prova a farlo manualmente.");
      }
    };

    return (
      <div className="p-4 mt-8 border rounded-lg border-base-300 bg-base-200">
        <h3 className="mb-4 text-lg font-semibold">Informazioni di debug</h3>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <p className="!mb-0">ID contenuto: {contentId}</p>
            <button
              onClick={handleCopyId}
              className="px-3 text-sm rounded-full btn btn-outline btn-primary btn-sm"
            >
              Copia ID
            </button>
          </div>

          <div>
            <h4 className="mb-2 font-medium">Analisi del contenuto</h4>
            <div className="pl-4">
              {exampleContents && exampleContents.length > 0 && (
                <details className="mt-2 !mb-[1em]">
                  <summary className="cursor-pointer">
                    Contenuti di esempio ({exampleContents.length})
                  </summary>
                  <ul className="pl-4 mt-2 space-y-8">
                    {exampleContents.map((example, index) => (
                      <li
                        key={index}
                        className="pb-8 border-b border-base-300 last:border-0"
                      >
                        <p className="font-medium">
                          Fonte:{" "}
                          <a
                            href={example.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {example.url}
                          </a>
                        </p>
                        <details>
                          <summary className="mb-4 cursor-pointer">
                            Contenuto pulito
                          </summary>
                          <div className="p-4 rounded bg-base-100">
                            <ReactMarkdown>
                              {example.cleanedContent}
                            </ReactMarkdown>
                          </div>
                        </details>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>

          {costs && (
            <div>
              <h4 className="mb-2 font-medium">Costi di generazione</h4>
              <div className="pl-4">
                <p>Costo totale: ${costs.total.toFixed(4)}</p>
                <p>Durata: {costs.durationInMinutes.toFixed(2)} minuti</p>
                {costs.details && costs.details.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer">
                      Dettaglio costi API
                    </summary>
                    <ul className="pl-4 mt-2 space-y-1">
                      {costs.details.map((cost, index) => (
                        <li key={index}>
                          {formatCostDescription(cost)}: ${cost.cost.toFixed(4)}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

DebugInfo.displayName = "DebugInfo";

export default DebugInfo;
